import React, { useState, useEffect } from 'react';
import { useToast } from '../../components/ui/Toast';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import UniversalNavbar from '../../components/ui/UniversalNavbar';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import ProgressIndicator from './components/ProgressIndicator';
import BasicInformationForm from './components/BasicInformationForm';
import TracksForm from './components/TracksForm';
import RolesForm from './components/RolesForm';
import ReviewSchemaForm from './components/ReviewSchemaForm';
import SubmissionFormBuilder from './components/SubmissionFormBuilder';
import PublishingSettings from './components/PublishingSettings';
import { conferenceService } from '../../services/conferenceService';
import reviewCriteriaService from '../../services/reviewCriteriaService';
import { supabase } from '../../lib/supabase';
import { formatDate } from '../../utils/formatters';

const ConferenceSetupWizard = () => {
  // Replaces alert(): non-blocking, styled, translatable, and errors
  // persist until dismissed instead of vanishing.
  const { success: toastSuccess, error: toastError } = useToast();
  const { t } = useTranslation('conferences');
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [draftConferenceId, setDraftConferenceId] = useState(null);

  const steps = [
    { id: 'basic', label: t('setupWizard.steps.basicInfo') },
    { id: 'tracks', label: t('setupWizard.steps.tracks') },
    { id: 'roles', label: t('setupWizard.steps.team') },
    { id: 'review', label: t('setupWizard.steps.reviewSchema') },
    { id: 'forms', label: t('setupWizard.steps.submissionForm') },
    { id: 'publish', label: t('setupWizard.steps.publishing') }
  ];

  const [basicInfo, setBasicInfo] = useState({
    conferenceName: '',
    shortName: '',
    conferenceType: '',
    subjectArea: '',
    website: '',
    locationType: '',
    venueName: '',
    location: '',
    startDate: '',
    endDate: '',
    description: '',
    callForPapers: ''
  });

  const [tracks, setTracks] = useState([]);
  const [keywords, setKeywords] = useState([]);
  const [roles, setRoles] = useState([]);
  const [reviewCriteria, setReviewCriteria] = useState([]);
  const [formFields, setFormFields] = useState([]);
  const [allowedFileTypes, setAllowedFileTypes] = useState('both');
  const [publishingSettings, setPublishingSettings] = useState({
    visibility: 'public',
    featured: false,
    publicRegistration: true,
    submissionStatus: 'open',
    paperDeadline: '',
    reviewDeadline: '',
    decisionDate: '',
    cameraReadyDeadline: '',
    confirmationEmails: true,
    reminderEmails: true,
    reviewAssignmentEmails: true,
    decisionEmails: true,
    minReviews: '3',
    enableBidding: true,
    doubleBlind: true,
    showOtherReviews: false
  });

  const [cfpFile, setCfpFile] = useState(null);
  const [bannerFile, setBannerFile] = useState(null);
  const [errors, setErrors] = useState({});
  const [isLoadingDraft, setIsLoadingDraft] = useState(true);

  // Load draft conference if draftId is in URL
  useEffect(() => {
    const loadDraftConference = async () => {
      try {
        const urlParams = new URLSearchParams(window.location.search);
        const draftId = urlParams?.get('draftId');
        
        if (draftId) {
          setDraftConferenceId(draftId);
          window.draftConferenceId = draftId; // Store for PublishingSettings access
          const conference = await conferenceService?.getById(draftId);
          
          if (conference) {
            // Populate basic info
            setBasicInfo({
              conferenceName: conference?.name || '',
              shortName: conference?.short_name || '',
              conferenceType: conference?.conference_type || '',
              subjectArea: conference?.subject_area || '',
              website: conference?.website || '',
              locationType: conference?.location_type || '',
              venueName: conference?.venue_name || '',
              location: conference?.location || '',
              startDate: conference?.start_date || '',
              endDate: conference?.end_date || '',
              description: conference?.description || '',
              callForPapers: conference?.call_for_papers || ''
            });

            // Populate keywords
            if (conference?.keywords && Array.isArray(conference?.keywords)) {
              setKeywords(conference?.keywords);
            }

            // Populate allowed file types
            if (conference?.allowedFileTypes || conference?.allowed_file_types) {
              setAllowedFileTypes(conference?.allowedFileTypes || conference?.allowed_file_types || 'both');
            }

            // Populate publishing settings
            setPublishingSettings({
              visibility: conference?.visibility || 'public',
              featured: conference?.featured || false,
              publicRegistration: conference?.public_registration !== false,
              submissionStatus: conference?.submission_status || 'open',
              paperDeadline: conference?.paper_deadline || '',
              reviewDeadline: conference?.review_deadline || '',
              decisionDate: conference?.decision_date || '',
              cameraReadyDeadline: conference?.camera_ready_deadline || '',
              confirmationEmails: true,
              reminderEmails: true,
              reviewAssignmentEmails: true,
              decisionEmails: true,
              minReviews: String(conference?.min_reviews || 3),
              enableBidding: conference?.enable_bidding !== false,
              doubleBlind: conference?.double_blind !== false,
              showOtherReviews: conference?.show_other_reviews || false
            });

            // Load tracks if they exist
            let conferenceId = conference?.id;
            if (conferenceId) {
              try {
                const tracksData = await conferenceService?.getTracks(conferenceId);
                if (tracksData && tracksData?.length > 0) {
                  setTracks(tracksData?.map(track => ({
                    name: track?.name,
                    description: track?.description
                  })));
                }
              } catch (trackError) {
                console.error('Error loading tracks:', trackError);
              }
            }
          }
        }
      } catch (error) {
        console.error('Error loading draft conference:', error);
        toastError(t('setupWizard.alerts.loadDraftFailed', { error: error?.message || 'Unknown error' }));
      } finally {
        setIsLoadingDraft(false);
      }
    };

    loadDraftConference();
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentStep]);

  const validateStep = () => {
    const newErrors = {};

    if (currentStep === 0) {
      if (!basicInfo?.conferenceName?.trim()) newErrors.conferenceName = t('setupWizard.validation.conferenceNameRequired');
      if (!basicInfo?.shortName?.trim()) newErrors.shortName = t('setupWizard.validation.shortNameRequired');
      if (!basicInfo?.conferenceType) newErrors.conferenceType = t('setupWizard.validation.conferenceTypeRequired');
      if (!basicInfo?.subjectArea) newErrors.subjectArea = t('setupWizard.validation.subjectAreaRequired');
      if (!basicInfo?.locationType) newErrors.locationType = t('setupWizard.validation.locationTypeRequired');
      if (!basicInfo?.startDate) newErrors.startDate = t('setupWizard.validation.startDateRequired');
      if (!basicInfo?.endDate) newErrors.endDate = t('setupWizard.validation.endDateRequired');
      if (!basicInfo?.description?.trim()) newErrors.description = t('setupWizard.validation.descriptionRequired');

      if (basicInfo?.startDate && basicInfo?.endDate && new Date(basicInfo.endDate) < new Date(basicInfo.startDate)) {
        newErrors.endDate = t('setupWizard.validation.endDateAfterStart');
      }
    }

    if (currentStep === 1) {
      if (tracks?.length === 0) newErrors.tracks = t('setupWizard.validation.tracksRequired');
    }

    if (currentStep === 2) {
      if (roles?.length === 0) newErrors.roles = t('setupWizard.validation.teamMemberRequired');
    }

    if (currentStep === 3) {
      if (reviewCriteria?.length === 0) newErrors.reviewCriteria = t('setupWizard.validation.reviewQuestionRequired');
    }

    if (currentStep === 4) {
      if (formFields?.length === 0) newErrors.formFields = t('setupWizard.validation.formFieldRequired');
    }

    if (currentStep === 5) {
      if (!publishingSettings?.visibility) newErrors.visibility = t('setupWizard.validation.visibilityRequired');
      if (!publishingSettings?.submissionStatus) newErrors.submissionStatus = t('setupWizard.validation.submissionStatusRequired');
      if (!publishingSettings?.paperDeadline) newErrors.paperDeadline = t('setupWizard.validation.paperDeadlineRequired');
      if (!publishingSettings?.reviewDeadline) newErrors.reviewDeadline = t('setupWizard.validation.reviewDeadlineRequired');
      if (!publishingSettings?.decisionDate) newErrors.decisionDate = t('setupWizard.validation.decisionDateRequired');
      if (!publishingSettings?.minReviews) newErrors.minReviews = t('setupWizard.validation.minReviewsRequired');
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleNext = () => {
    if (validateStep()) {
      if (currentStep < steps?.length - 1) {
        setCurrentStep(currentStep + 1);
      }
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSaveDraft = async () => {
    try {
      setIsSaving(true);
      setErrors({});

      const conferenceData = {
        conferenceName: basicInfo?.conferenceName,
        shortName: basicInfo?.shortName,
        conferenceType: basicInfo?.conferenceType,
        subjectArea: basicInfo?.subjectArea,
        website: basicInfo?.website,
        locationType: basicInfo?.locationType,
        venueName: basicInfo?.venueName,
        location: basicInfo?.location,
        startDate: basicInfo?.startDate,
        endDate: basicInfo?.endDate,
        description: basicInfo?.description,
        callForPapers: basicInfo?.callForPapers,
        paperDeadline: publishingSettings?.paperDeadline,
        reviewDeadline: publishingSettings?.reviewDeadline,
        decisionDate: publishingSettings?.decisionDate,
        cameraReadyDeadline: publishingSettings?.cameraReadyDeadline,
        visibility: publishingSettings?.visibility,
        featured: publishingSettings?.featured,
        publicRegistration: publishingSettings?.publicRegistration,
        minReviews: parseInt(publishingSettings?.minReviews) || 3,
        enableBidding: publishingSettings?.enableBidding,
        doubleBlind: publishingSettings?.doubleBlind,
        showOtherReviews: publishingSettings?.showOtherReviews,
        allowedFileTypes: allowedFileTypes,
        keywords: keywords,
        status: 'draft'
      };

      let conferenceId;

      // Update existing draft or create new draft
      if (draftConferenceId) {
        conferenceId = draftConferenceId;
        await conferenceService?.update(conferenceId, conferenceData);
      } else {
        const result = await conferenceService?.create(conferenceData);
        conferenceId = result?.id;
        setDraftConferenceId(conferenceId);
      }

      // Upload banner image if selected
      if (conferenceId && bannerFile) {
        try {
          const bannerPath = await conferenceService?.uploadBannerImage(bannerFile, conferenceId);
          await conferenceService?.update(conferenceId, { bannerImageUrl: bannerPath });
          setBannerFile(null);
        } catch (bannerError) {
          console.error('Error uploading banner image:', bannerError);
        }
      }

      // Upload CFP PDF if selected
      if (conferenceId && cfpFile) {
        try {
          const cfpPath = await conferenceService?.uploadCallForPapersPDF(cfpFile, conferenceId);
          await conferenceService?.update(conferenceId, { callForPapersUrl: cfpPath });
          setCfpFile(null);
        } catch (cfpError) {
          console.error('Error uploading CFP PDF:', cfpError);
        }
      }

      // Add or update tracks
      if (conferenceId && tracks?.length > 0) {
        for (const track of tracks) {
          await conferenceService?.addTrack(conferenceId, track);
        }
      }

      // Save review criteria if on review step or later
      if (currentStep >= 3 && reviewCriteria?.length > 0) {
        try {
          await reviewCriteriaService?.bulkCreate(conferenceId, reviewCriteria);
        } catch (criteriaError) {
          console.error('Error saving review criteria:', criteriaError);
        }
      }

      toastSuccess(t('setupWizard.alerts.draftSaved'));
      navigate('/user-dashboard', { state: { refresh: true } });
    } catch (error) {
      console.error('Save draft error:', error);
      toastError(t('setupWizard.alerts.draftSaveFailed', { error: error?.message || 'Unknown error' }));
    } finally {
      setIsSaving(false);
    }
  };

  const handlePublish = async () => {
    if (!validateStep()) {
      return;
    }

    try {
      setIsSaving(true);
      setErrors({});

      const conferenceData = {
        conferenceName: basicInfo?.conferenceName,
        shortName: basicInfo?.shortName,
        conferenceType: basicInfo?.conferenceType,
        subjectArea: basicInfo?.subjectArea,
        website: basicInfo?.website,
        locationType: basicInfo?.locationType,
        venueName: basicInfo?.venueName,
        location: basicInfo?.location,
        startDate: basicInfo?.startDate,
        endDate: basicInfo?.endDate,
        description: basicInfo?.description,
        callForPapers: basicInfo?.callForPapers,
        paperDeadline: publishingSettings?.paperDeadline,
        reviewDeadline: publishingSettings?.reviewDeadline,
        decisionDate: publishingSettings?.decisionDate,
        cameraReadyDeadline: publishingSettings?.cameraReadyDeadline,
        visibility: publishingSettings?.visibility,
        featured: publishingSettings?.featured,
        publicRegistration: publishingSettings?.publicRegistration,
        minReviews: parseInt(publishingSettings?.minReviews) || 3,
        enableBidding: publishingSettings?.enableBidding,
        doubleBlind: publishingSettings?.doubleBlind,
        showOtherReviews: publishingSettings?.showOtherReviews,
        allowedFileTypes: allowedFileTypes,
        keywords: keywords
        // NOTE: saved as draft first; going live happens via the paywall RPC below.
      };

      let conferenceId;

      // Update existing draft or create new conference
      if (draftConferenceId) {
        conferenceId = draftConferenceId;
        await conferenceService?.update(conferenceId, conferenceData);
      } else {
        const result = await conferenceService?.create(conferenceData);
        conferenceId = result?.id;
      }

      // Upload banner image if selected
      if (conferenceId && bannerFile) {
        try {
          const bannerPath = await conferenceService?.uploadBannerImage(bannerFile, conferenceId);
          await conferenceService?.update(conferenceId, { bannerImageUrl: bannerPath });
          setBannerFile(null);
        } catch (bannerError) {
          console.error('Error uploading banner image:', bannerError);
        }
      }

      // Upload CFP PDF if selected
      if (conferenceId && cfpFile) {
        try {
          const cfpPath = await conferenceService?.uploadCallForPapersPDF(cfpFile, conferenceId);
          await conferenceService?.update(conferenceId, { callForPapersUrl: cfpPath });
          setCfpFile(null);
        } catch (cfpError) {
          console.error('Error uploading CFP PDF:', cfpError);
        }
      }

      // Add or update tracks
      if (conferenceId && tracks?.length > 0) {
        for (const track of tracks) {
          await conferenceService?.addTrack(conferenceId, track);
        }
      }

      // Paywall gate: going live consumes an entitlement (first free, then a
      // credit or an active annual plan). If the user has none left, send them
      // to checkout — the conference is safely saved as a draft.
      try {
        const { billingService } = await import('../../services/billingService');
        await billingService?.publishConference(conferenceId);
      } catch (payErr) {
        if ((payErr?.message || '')?.includes('payment_required')) {
          navigate(`/billing?conferenceId=${conferenceId}&intent=publish`);
          return;
        }
        throw payErr;
      }

      // Send email notification to conference creator
      try {
        const { data: { user } } = await supabase?.auth?.getUser();
        if (user) {
          const { data: profile } = await supabase
            ?.from('user_profiles')
            ?.select('first_name, last_name, email')
            ?.eq('id', user?.id)
            ?.single();

          if (profile?.email) {
            const { notificationService } = await import('../../services/notificationService');
            await notificationService?.sendEmail(
              profile?.email,
              `Conference Published: ${basicInfo?.conferenceName}`,
              `Dear ${profile?.first_name} ${profile?.last_name},\n\nYour conference "${basicInfo?.conferenceName}" has been successfully published and is now visible on Lynk.\n\nConference Details:\n- Short Name: ${basicInfo?.shortName}\n- Start Date: ${formatDate(basicInfo?.startDate)}\n- Paper Deadline: ${publishingSettings?.paperDeadline ? formatDate(publishingSettings?.paperDeadline) : 'Not set'}\n\nYou can manage your conference from the dashboard.\n\nBest regards,\nArcheditech Team`,
              'conference_created'
            );
          }
        }
      } catch (emailError) {
        console.error('Failed to send conference creation email:', emailError);
        // Don't fail the publish if email fails
      }

      toastSuccess(t('setupWizard.alerts.conferencePublished'));
      navigate('/user-dashboard', { state: { refresh: true } });
    } catch (error) {
      console.error('Publish conference error:', error);
      toastError(t('setupWizard.alerts.publishFailed', { error: error?.message || 'Unknown error' }));
    } finally {
      setIsSaving(false);
    }
  };

  const handleBasicInfoChange = (e) => {
    const { name, value } = e?.target;
    setBasicInfo({ ...basicInfo, [name]: value });
    if (errors?.[name]) {
      setErrors({ ...errors, [name]: '' });
    }
  };

  const handlePublishingSettingsChange = (e) => {
    const { name, value } = e?.target;
    setPublishingSettings({ ...publishingSettings, [name]: value });
    if (errors?.[name]) {
      setErrors({ ...errors, [name]: '' });
    }
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e?.target;
    setPublishingSettings({ ...publishingSettings, [name]: checked });
  };

  if (isLoadingDraft) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Icon name="Loader" size={48} className="animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">{t('setupWizard.loading')}</p>
        </div>
      </div>
    );
  }

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <BasicInformationForm
            formData={basicInfo}
            onChange={handleBasicInfoChange}
            errors={errors}
            cfpFile={cfpFile}
            onCfpFileChange={setCfpFile}
            bannerFile={bannerFile}
            onBannerFileChange={setBannerFile}
          />
        );
      case 1:
        return (
          <TracksForm
            tracks={tracks}
            onTracksChange={setTracks}
            errors={errors}
          />
        );
      case 2:
        return (
          <RolesForm
            roles={roles}
            onRolesChange={setRoles}
            errors={errors}
          />
        );
      case 3:
        return (
          <ReviewSchemaForm
            reviewCriteria={reviewCriteria}
            onReviewCriteriaChange={setReviewCriteria}
            errors={errors}
          />
        );
      case 4:
        return (
          <SubmissionFormBuilder
            formFields={formFields}
            onFormFieldsChange={setFormFields}
            keywords={keywords}
            onKeywordsChange={setKeywords}
            allowedFileTypes={allowedFileTypes}
            onAllowedFileTypesChange={setAllowedFileTypes}
            errors={errors}
          />
        );
      case 5:
        return (
          <PublishingSettings
            settings={publishingSettings}
            onChange={handlePublishingSettingsChange}
            errors={errors}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <UniversalNavbar />
      <main className="page-shell py-8">
        <ProgressIndicator currentStep={currentStep} steps={steps} />

        <div className="flex-1 overflow-y-auto">
          <div className="page-shell px-4 md:px-6 lg:px-8 py-6 md:py-8">
            {renderStepContent()}
          </div>
        </div>

        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 bg-card rounded-lg border border-border p-4 md:p-6">
          <Button
            variant="outline"
            onClick={handleSaveDraft}
            loading={isSaving}
            iconName="Save"
            iconPosition="left"
            className="w-full sm:w-auto"
          >
            {t('setupWizard.buttons.saveDraft')}
          </Button>

          <div className="flex items-center gap-3 w-full sm:w-auto">
            {currentStep > 0 && (
              <Button
                variant="outline"
                onClick={handlePrevious}
                iconName="ChevronLeft"
                iconPosition="left"
                className="flex-1 sm:flex-initial"
              >
                {t('setupWizard.buttons.previous')}
              </Button>
            )}

            {currentStep < steps?.length - 1 ? (
              <Button
                variant="default"
                onClick={handleNext}
                iconName="ChevronRight"
                iconPosition="right"
                className="flex-1 sm:flex-initial"
              >
                {t('setupWizard.buttons.nextStep')}
              </Button>
            ) : (
              <Button
                variant="default"
                onClick={handlePublish}
                loading={isSaving}
                iconName="CheckCircle"
                iconPosition="left"
                className="flex-1 sm:flex-initial"
              >
                {t('setupWizard.buttons.publishConference')}
              </Button>
            )}
          </div>
        </div>

        <div className="mt-6 bg-muted/50 rounded-lg p-4 md:p-6">
          <div className="flex gap-3 md:gap-4">
            <Icon name="Info" size={20} className="text-primary flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="text-sm md:text-base font-semibold text-foreground mb-2">{t('setupWizard.help.title')}</h4>
              <p className="text-xs md:text-sm text-muted-foreground">
                {t('setupWizard.help.description')}
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ConferenceSetupWizard;